import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { brokerService, buildImageUrl } from '../../services/api';

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h2.fontSize};
  font-weight: ${({ theme }) => theme.typography.h2.fontWeight};
`;

const AddButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const SearchContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const BrokerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const BrokerCard = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const BrokerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const BrokerPhoto = styled.div<{ $photo?: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ $photo, theme }) => 
    $photo ? `url(${$photo}) center/cover` : theme.colors.primary
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
`;

const BrokerInfo = styled.div`
  flex: 1;
`;

const BrokerName = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.h6.fontSize};
  font-weight: ${({ theme }) => theme.typography.h6.fontWeight};
`;

const BrokerEmail = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
`;

const BrokerDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

const StatusBadge = styled.span<{ $active: boolean; $isLeader?: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ $active, $isLeader, theme }) => {
    if ($isLeader) return theme.colors.warning;
    return $active ? theme.colors.success : theme.colors.error;
  }};
  color: white;
`;

const BrokerActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  background: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'danger': return theme.colors.error;
      case 'secondary': return theme.colors.text.secondary;
      default: return theme.colors.primary;
    }
  }};
  
  color: white;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Modal = styled.div<{ $show: boolean }>`
  display: ${({ $show }) => $show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.h4.fontSize};
  font-weight: ${({ theme }) => theme.typography.h4.fontWeight};
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:invalid {
    border-color: ${({ theme }) => theme.colors.error};
  }
`;

const TextArea = styled.textarea`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const FormActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const SubmitButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: ${({ theme }) => theme.colors.text.secondary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

const PhotoUploadArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
`;

const PhotoPreview = styled.div<{ $photo?: string }>`
  width: 100px;
  height: 100px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $photo, theme }) => 
    $photo ? `url(${$photo}) center/cover` : theme.colors.border
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled.label`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }
`;

interface Broker {
  id: number;
  name: string;
  email: string;
  phone: string;
  creci: string;
  cpf?: string;
  specialty?: string;
  experience_years: number;
  commission_rate: number;
  bio?: string;
  photo?: string;
  address?: string;
  is_active: boolean;
  is_team_leader: boolean;
  is_partner: boolean;
  show_on_site: boolean;
  created_at: string;
  active_leads_count: number;
  total_leads_count: number;
}

interface BrokerFormData {
  name: string;
  email: string;
  phone: string;
  creci: string;
  cpf: string;
  specialty: string;
  experience_years: number;
  commission_rate: number;
  bio: string;
  address: string;
  is_active: boolean;
  is_team_leader: boolean;
  is_partner: boolean;
  show_on_site: boolean;
}

const AdminBrokers: React.FC = () => {
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBroker, setEditingBroker] = useState<Broker | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [formData, setFormData] = useState<BrokerFormData>({
    name: '',
    email: '',
    phone: '',
    creci: '',
    cpf: '',
    specialty: '',
    experience_years: 0,
    commission_rate: 0,
    bio: '',
    address: '',
    is_active: true,
    is_team_leader: false,
    is_partner: false,
    show_on_site: true
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const loadBrokers = async () => {
    try {
      const data = await brokerService.getAll();
      const normalized: Broker[] = (data || []).map((broker: any) => ({
        ...broker,
        phone: (broker.phone || '').toString().replace(/\D/g, ''),
        cpf: (broker.cpf || '').toString().replace(/\D/g, ''),
        experience_years: Number(broker.experience_years) || 0,
        commission_rate: typeof broker.commission_rate === 'number'
          ? broker.commission_rate
          : Number(broker.commission_rate || 0),
      })) as Broker[];
      setBrokers(normalized);
    } catch (error) {
      console.error('Erro ao carregar corretores:', error);
      toast.error('Erro ao carregar corretores');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadBrokers();
      setLoading(false);
    };

    loadData();
  }, []);

  const handleOpenModal = (broker?: Broker) => {
    if (broker) {
      setEditingBroker(broker);
      setFormData({
        name: broker.name,
        email: broker.email,
        phone: (broker.phone || '').replace(/\D/g, ''),
        creci: broker.creci,
        cpf: (broker.cpf || '').replace(/\D/g, ''),
        specialty: broker.specialty || '',
        experience_years: Number(broker.experience_years) || 0,
        commission_rate: typeof broker.commission_rate === 'number'
          ? broker.commission_rate
          : Number(broker.commission_rate || 0),
        bio: broker.bio || '',
        address: broker.address || '',
        is_active: broker.is_active,
        is_team_leader: broker.is_team_leader,
        is_partner: broker.is_partner,
        show_on_site: broker.show_on_site
      });
      setPhotoPreview(broker.photo ? buildImageUrl(broker.photo) : '');
    } else {
      setEditingBroker(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        creci: '',
        cpf: '',
        specialty: '',
        experience_years: 0,
        commission_rate: 0,
        bio: '',
        address: '',
        is_active: true,
        is_team_leader: false,
        is_partner: false,
        show_on_site: true
      });
      setPhotoPreview('');
    }
    setPhotoFile(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBroker(null);
    setPhotoFile(null);
    setPhotoPreview('');
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '').slice(0, 11);
      setFormData(prev => ({ ...prev, phone: cleaned }));
    } else if (name === 'cpf') {
      const cleaned = value.replace(/\D/g, '').slice(0, 11);
      setFormData(prev => ({ ...prev, cpf: cleaned }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let brokerId: number;
      const brokerData = { ...formData, cpf: formData.cpf || null };
      
      if (editingBroker) {
        await brokerService.update(editingBroker.id, brokerData);
        brokerId = editingBroker.id;
        toast.success('Corretor atualizado com sucesso!');
      } else {
        const newBroker = await brokerService.create(brokerData);
        brokerId = newBroker.id;
        toast.success('Corretor criado com sucesso!');
      }
      
      // Upload da foto se houver
      if (photoFile) {
        try {
          await brokerService.uploadPhoto(brokerId, photoFile);
          toast.success('Foto do corretor enviada com sucesso!');
        } catch (photoError) {
          console.error('Erro ao enviar foto:', photoError);
          toast.error('Erro ao enviar foto do corretor');
        }
      }
      
      handleCloseModal();
      await loadBrokers();
    } catch (error: any) {
      console.error('Erro ao salvar corretor:', error);
      const message = error.response?.data?.detail || 'Erro ao salvar corretor';
      toast.error(message);
    }
  };

  const handleDelete = async (broker: Broker) => {
    if (!window.confirm(`Tem certeza que deseja desativar o corretor ${broker.name}?`)) {
      return;
    }

    try {
      await brokerService.delete(broker.id);
      toast.success('Corretor desativado com sucesso!');
      await loadBrokers();
    } catch (error: any) {
      console.error('Erro ao desativar corretor:', error);
      const message = error.response?.data?.detail || 'Erro ao desativar corretor';
      toast.error(message);
    }
  };

  const handleActivate = async (broker: Broker) => {
    try {
      await brokerService.activate(broker.id);
      toast.success('Corretor reativado com sucesso!');
      await loadBrokers();
    } catch (error: any) {
      console.error('Erro ao reativar corretor:', error);
      const message = error.response?.data?.detail || 'Erro ao reativar corretor';
      toast.error(message);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const formatCPF = (cpf: string) => {
    const cleaned = cpf.replace(/\D/g, '');
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Carregando...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Gerenciar Corretores</Title>
        <AddButton onClick={() => handleOpenModal()}>
          + Novo Corretor
        </AddButton>
      </Header>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Buscar por nome, email ou CRECI..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterSelect
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
        >
          <option value="all">Todos</option>
          <option value="active">Apenas Ativos</option>
          <option value="inactive">Apenas Inativos</option>
        </FilterSelect>
      </SearchContainer>

      <BrokerGrid>
        {brokers.map((broker) => (
          <BrokerCard key={broker.id}>
            <BrokerHeader>
              <BrokerPhoto $photo={broker.photo ? buildImageUrl(broker.photo) : undefined}>
                {!broker.photo && getInitials(broker.name)}
              </BrokerPhoto>
              <BrokerInfo>
                <BrokerName>{broker.name}</BrokerName>
                <BrokerEmail>{broker.email}</BrokerEmail>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <StatusBadge $active={broker.is_active} $isLeader={broker.is_team_leader}>
                    {broker.is_team_leader ? 'Líder' : broker.is_active ? 'Ativo' : 'Inativo'}
                  </StatusBadge>
                  {broker.is_partner && (
                    <StatusBadge $active={true} style={{ background: '#6c5ce7' }}>
                      Parceiro
                    </StatusBadge>
                  )}
                  {!broker.show_on_site && (
                    <StatusBadge $active={false} style={{ background: '#a0a0a0' }}>
                      Oculto no Site
                    </StatusBadge>
                  )}
                </div>
              </BrokerInfo>
            </BrokerHeader>

            <BrokerDetails>
              <DetailItem>
                <DetailLabel>CRECI</DetailLabel>
                <DetailValue>{broker.creci}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Telefone</DetailLabel>
                <DetailValue>{formatPhone(broker.phone)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Especialidade</DetailLabel>
                <DetailValue>{broker.specialty || 'Não informado'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Experiência</DetailLabel>
                <DetailValue>{broker.experience_years} anos</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Leads Ativos</DetailLabel>
                <DetailValue>{broker.active_leads_count}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Total Leads</DetailLabel>
                <DetailValue>{broker.total_leads_count}</DetailValue>
              </DetailItem>
            </BrokerDetails>

            <BrokerActions>
              <ActionButton onClick={() => handleOpenModal(broker)}>
                Editar
              </ActionButton>
              {broker.is_active ? (
                <ActionButton 
                  $variant="danger" 
                  onClick={() => handleDelete(broker)}
                >
                  Desativar
                </ActionButton>
              ) : (
                <ActionButton 
                  $variant="secondary" 
                  onClick={() => handleActivate(broker)}
                >
                  Ativar
                </ActionButton>
              )}
            </BrokerActions>
          </BrokerCard>
        ))}
      </BrokerGrid>

      {brokers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Nenhum corretor encontrado.
        </div>
      )}

      <Modal $show={showModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingBroker ? 'Editar Corretor' : 'Novo Corretor'}
            </ModalTitle>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>Nome *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Telefone *</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formatPhone(formData.phone)}
                  onChange={handleInputChange}
                  placeholder="(11) 99999-9999"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>CRECI *</Label>
                <Input
                  type="text"
                  name="creci"
                  value={formData.creci}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>CPF (opcional)</Label>
                <Input
                  type="text"
                  name="cpf"
                  value={formatCPF(formData.cpf)}
                  onChange={handleInputChange}
                  placeholder="000.000.000-00"
                />
              </FormGroup>
              <FormGroup>
                <Label>Especialidade</Label>
                <Select
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleInputChange}
                >
                  <option value="">Selecione...</option>
                  <option value="vendas">Vendas</option>
                  <option value="locacao">Locação</option>
                  <option value="comercial">Comercial</option>
                  <option value="residencial">Residencial</option>
                  <option value="rural">Rural</option>
                  <option value="lancamentos">Lançamentos</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Anos de Experiência</Label>
                <Input
                  type="number"
                  name="experience_years"
                  value={formData.experience_years}
                  onChange={handleInputChange}
                  min="0"
                />
              </FormGroup>
              <FormGroup>
                <Label>Taxa de Comissão (%)</Label>
                <Input
                  type="number"
                  name="commission_rate"
                  value={formData.commission_rate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Endereço</Label>
              <Input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>Biografia</Label>
              <TextArea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                placeholder="Conte um pouco sobre a experiência e especialidades do corretor..."
              />
            </FormGroup>

            <FormGroup>
              <Label>Foto do Corretor</Label>
              <PhotoUploadArea>
                <PhotoPreview $photo={photoPreview}>
                  {!photoPreview && 'Sem foto'}
                </PhotoPreview>
                <div>
                  <FileInput
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                  <UploadButton htmlFor="photo-upload">
                    {photoPreview ? 'Alterar Foto' : 'Selecionar Foto'}
                  </UploadButton>
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: '#666' }}>
                    JPG, PNG ou GIF (máx. 5MB)
                  </p>
                </div>
              </PhotoUploadArea>
            </FormGroup>

            <FormRow>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <Label>Corretor Ativo</Label>
              </CheckboxGroup>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  name="is_team_leader"
                  checked={formData.is_team_leader}
                  onChange={handleInputChange}
                />
                <Label>Líder de Equipe</Label>
              </CheckboxGroup>
            </FormRow>

            <FormRow>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  name="is_partner"
                  checked={formData.is_partner}
                  onChange={handleInputChange}
                />
                <Label>Parceiro</Label>
              </CheckboxGroup>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  name="show_on_site"
                  checked={formData.show_on_site}
                  onChange={handleInputChange}
                />
                <Label>Mostrar no Site</Label>
              </CheckboxGroup>
            </FormRow>

            <FormActions>
              <CancelButton type="button" onClick={handleCloseModal}>
                Cancelar
              </CancelButton>
              <SubmitButton type="submit">
                {editingBroker ? 'Atualizar' : 'Criar'} Corretor
              </SubmitButton>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AdminBrokers; 
